import { MM_PAGE_SIZE_DEFAULT } from '../config/env';
import { MmPage, MmPageQuery, MmTaskFilters, MmTaskFiltersQuery } from '../types/dto';

export const MM_QUERY_PARAM_NAMES = {
  page: 'page',
  size: 'size',
  sortBy: 'sortBy',
  sortDirection: 'sortDirection',
  search: 'q',
};

export interface MmPaginationState {
  current: number;
  pageSize: number;
  total: number;
}

export function createPaginationState(pageSize?: number): MmPaginationState {
  return {
    current: 1,
    pageSize: pageSize === undefined ? MM_PAGE_SIZE_DEFAULT : pageSize,
    total: 0,
  };
}

export interface MmSortingState {
  field: string | null;
  order: 'asc' | 'desc' | null;
}

export function createSortingState(field?: string, order?: 'asc' | 'desc'): MmSortingState {
  return {
    field: field === undefined ? null : field,
    order: order === undefined ? null : order,
  };
}

export function readAntSorter(sorter: any, fallback: MmSortingState): MmSortingState {
  if (sorter && sorter.field && sorter.order) {
    return {
      field: String(sorter.field),
      order: sorter.order === 'descend' ? 'desc' : 'asc',
    };
  }

  return { field: fallback.field, order: fallback.order };
}

export function buildPageQuery(
  pagination: MmPaginationState,
  sorting?: MmSortingState,
  fieldNameMap?: { [key: string]: string },
): { [key: string]: any } {
  const params: { [key: string]: any } = {};

  params[MM_QUERY_PARAM_NAMES.page] = pagination.current - 1;
  params[MM_QUERY_PARAM_NAMES.size] = pagination.pageSize;

  if (sorting && sorting.field !== null && sorting.order !== null) {
    const mapped = fieldNameMap && fieldNameMap[sorting.field] ? fieldNameMap[sorting.field] : sorting.field;

    params[MM_QUERY_PARAM_NAMES.sortBy] = mapped;
    params[MM_QUERY_PARAM_NAMES.sortDirection] = sorting.order;
  }

  return params;
}

export function buildLimitOffsetQuery(pagination: MmPaginationState): { limit: number; offset: number } {
  return {
    limit: pagination.pageSize,
    offset: (pagination.current - 1) * pagination.pageSize,
  };
}

export function toPageQuery(pagination: MmPaginationState, sorting?: MmSortingState): MmPageQuery {
  return {
    page: pagination.current - 1,
    size: pagination.pageSize,
    sortBy: sorting && sorting.field !== null ? sorting.field : null,
    sortDirection: sorting && sorting.order !== null ? sorting.order : null,
  };
}

/** Два конверта ответа: имена items и total читаются первыми и побеждают при обоих написаниях */
export function readPage<T>(data: any): MmPage<T> {
  if (!data || typeof data !== 'object') {
    return { content: [], totalElements: 0, totalKnown: false, hasMore: null };
  }

  const items = Array.isArray(data.items)
    ? data.items
    : (Array.isArray(data.content) ? data.content : []);

  const totalRaw = typeof data.total === 'number' ? data.total : data.totalElements;
  const totalKnown = typeof totalRaw === 'number';

  return {
    content: items as T[],
    totalElements: totalKnown ? (totalRaw as number) : items.length,
    totalKnown: totalKnown,
    hasMore: typeof data.hasMore === 'boolean' ? data.hasMore : null,
  };
}

/** Порядок источников не менять: hasMore, затем totalElements при известном итоге, затем эвристика. */
export function hasNextPage<T>(page: MmPage<T>, pagination: MmPaginationState): boolean {
  if (page.hasMore !== null) {
    return page.hasMore;
  }

  if (page.totalKnown) {
    return pagination.current * pagination.pageSize < page.totalElements;
  }

  return page.content.length >= pagination.pageSize;
}

export function resolvePagerTotal<T>(page: MmPage<T>, pagination: MmPaginationState): number {
  if (page.totalKnown) {
    return page.totalElements;
  }

  const consumed = (pagination.current - 1) * pagination.pageSize + page.content.length;

  return hasNextPage(page, pagination) ? consumed + 1 : consumed;
}

/** Имена фильтров в запросе и в теле разные: subcategoryIds против subCategories */
export function serializeFiltersToQuery(filters: MmTaskFilters): MmTaskFiltersQuery {
  return {
    formatIds: filters.formats.slice(),
    regionIds: filters.regions.slice(),
    clusterIds: filters.clusters.slice(),
    groupIds: filters.groups.slice(),
    categoryIds: filters.categories.slice(),
    subcategoryIds: filters.subCategories.slice(),
  };
}

export function createEmptyTaskFilters(): MmTaskFilters {
  return {
    formats: [],
    regions: [],
    clusters: [],
    groups: [],
    categories: [],
    subCategories: [],
  };
}
